import cn from 'classnames';
import { noop } from 'lodash';
import React, { forwardRef, MouseEventHandler, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useErrorTrackingContext } from '../../contexts/errorTrackingContext';
import { useImageVisible } from '../../hooks/useImageVisible';
import { imageToDataUri } from '../../utils/image';
import { notifyIframeHeight } from '../../utils/notifyIframeHeight';
import { resolveImageSrc } from '../../utils/resolveImageSrc';
import styles from './Image.module.css';

export interface ImageSchema {
  previewHeight?: number;
  previewWidth?: number;
  previewSrc?: string;
  preview?: string;
  aspect?: number | null;
  height?: number;
  width?: number;
  src: string;
  originalSrc?: string;
}

export interface ImageProps {
  image: ImageSchema;
  className?: string;
  style?: React.CSSProperties;
  /** TODO: will not work when className is specified */
  rounded?: boolean;
  onClick?: MouseEventHandler;
  isMediaFile?: boolean;
  onLoad?: React.EventHandler<React.SyntheticEvent<HTMLImageElement>>;
  onPreviewLoad?: React.EventHandler<React.SyntheticEvent>;
  onImageVisible?: (element: HTMLImageElement | null) => void;
  draggable?: boolean;
}

const ImageComponent = forwardRef<HTMLImageElement | null, ImageProps>(
  (
    {
      onClick,
      image,
      className,
      style,
      rounded,
      isMediaFile = false,
      onLoad = noop,
      onPreviewLoad = noop,
      onImageVisible,
      draggable,
    },
    ref,
  ) => {
    const [isLoaded, setLoaded] = useState(false);
    const [previewUri, setPreviewUri] = useState<string>();
    const { src: imageSrc, width, height, preview } = image || {};
    const { originalSrc: imageOriginalSrc = imageSrc } = image || {};

    const { sendError } = useErrorTrackingContext();

    const { imageRef } = useImageVisible({ onImageVisible });

    useImperativeHandle<HTMLImageElement | null, HTMLImageElement | null>(ref, () => imageRef.current, []);

    const captureLoadError =
      (imgSrc: string) =>
      (...args: Parameters<OnErrorEventHandlerNonNull>) => {
        const error = args[4];

        if (error) {
          sendError(error, { additionalInfo: { imgSrc }, severity: 'MINOR' });
        }
      };

    useEffect(() => {
      const img = new Image();
      img.onload = function (e) {
        setPreviewUri(imageToDataUri(this, width!, height!)); //@Check-Undefined

        onPreviewLoad(e);
      };
      img.src = preview ?? '';

      if (img.src) img.onerror = captureLoadError(img.src);
    }, [preview, width, height]);

    const classNames = cn(className || styles.image, { [styles.rounded]: rounded });
    const src = resolveImageSrc(imageSrc);
    const originalSrc = resolveImageSrc(imageOriginalSrc);

    const hasPreview = preview != null;
    const showPreview = hasPreview && !isLoaded;
    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true);
      notifyIframeHeight();

      onLoad(e);
    };
    const onError = () => {
      if (src) return captureLoadError(src);
      return;
    };

    const imageStyle = useMemo(() => {
      return showPreview ? { display: 'none' } : undefined;
    }, [showPreview]);

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div onClick={onClick} className={classNames} style={style}>
        {showPreview && (
          <img
            className={styles.preview}
            src={previewUri || filler({ width: width!, height: height! })}
            alt=""
            draggable={draggable}
          />
        )}
        <img
          ref={imageRef}
          src={isMediaFile ? src : originalSrc}
          onLoad={handleLoad}
          onError={onError}
          style={imageStyle}
          draggable={draggable}
          alt=""
        />
      </div>
    );
  },
);

ImageComponent.displayName = 'ImageComponent';

function filler({ width, height }: { width: number; height: number }) {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}' width="${width}" height="${height}" %3E%3C/svg%3E`;
}

export default ImageComponent;
